<template>
  <b-sidebar id="sidebar" :visible="isSidebarActive" bg-variant="white" sidebar-class="sidebar-lg" shadow backdrop
    no-header no-close-on-backdrop right @shown="loadData" @hidden="resetForm"
    @change="(val) => $emit('update:is-sidebar-active', val)">
    <template #default="{ hide }">
      <!-- Header -->
      <div class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1">
        <h5 class="mb-0 text-capitalize">
          {{
            isAdd
              ? `${$t("actions.add")} ${$tc("profiles.title", 0)}`
              : `${$t("actions.edit")} ${$tc("profiles.title", 0)}`
          }}
        </h5>

        <feather-icon class="ml-1 cursor-pointer" icon="XIcon" size="16" @click="hide" />
      </div>

      <!-- BODY -->
      <validation-observer #default="{ handleSubmit }" ref="refFormObserver">
        <!-- Form -->
        <b-form class="p-2" @submit.prevent="handleSubmit(onSubmit)" @reset.prevent="resetForm">
          <!-- type -->
          <validation-provider #default="validationContext" name="type" rules="required">
            <b-form-group class="text-capitalize" :label="$tc('profiles.title', 1)" label-for="type">
              <b-form-input id="type" v-model="itemData.type" :state="getValidationState(validationContext)" trim
                :disabled="!isAdd && (itemData.type == 'SuperAdmin' || itemData.type == 'MasterAdmin')"
                data-cy="profile" />

              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- description -->
          <validation-provider
            #default="validationContext"
            name="description"
            rules="required|max:512"
          >
            <b-form-group
              class="text-capitalize"
              :label="$t('profiles.description')"
              label-for="type"
            >
              <b-form-textarea
                id="description"
                v-model="itemData.description"
                :state="getValidationState(validationContext)"
                trim
                rows="2"
                max-rows="6"
                data-cy="description"
              />

              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- layouts -->
          <validation-provider #default="validationContext" name="layouts" rules="required">
            <b-form-group class="text-capitalize" :label="$tc('layout_builder.title', 1)" label-for="layouts"
              :state="getValidationState(validationContext)">
              <v-select v-model="itemData.layouts" :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="layoutsOptions" :clearable="true" :reduce="(val) => val.value" input-id="layouts" multiple
                data-cy="layouts" />
              <b-form-invalid-feedback :state="getValidationState(validationContext)">
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- forced_layouts -->
          <validation-provider name="forced_layouts" rules="required">
            <b-form-checkbox id="forced_layouts" v-model="itemData.forced_layouts" name="forced_layouts" value="1"
              unchecked-value="0" class="my-1 text-capitalize" switch>
              {{ $t('profiles.forced_layouts') }}
            </b-form-checkbox>
          </validation-provider>

          <div class="mb-3" v-if="isDataLoaded">
            <div class="d-flex align-items-center mb-1" v-if="isDataLoaded">
              <input type="checkbox" :checked="allSelected" @change="toggleAll" data-cy="allPermissions" />
              <span class="text-capitalize" style="margin-left: 5px; margin-bottom: -2px;">{{
                $t("actions.select_deselect_all") }}</span>
            </div>

            <treeselect v-model="selectedPermissions" :value-consists-of="valueConsistsOf" :multiple="true"
              :options="permissionsOptionsTree" :flat="false" :nested="true" :limit="3" v-if="isDataLoaded" />
          </div>

          <validation-provider #default="validationContext" :name="$t('profiles.session_time')" rules="required">
            <b-form-group class="text-capitalize" :label="$t('profiles.session_time')" label-for="token_lifetime">
              <b-input-group>
                <b-input-group-prepend>
                  <b-button variant="primary" @click="itemData.token_lifetime--"
                    :disabled="itemData.token_lifetime <= 0 || itemData.token_lifetime == null">-</b-button>
                </b-input-group-prepend>

                <b-form-input class="text-center" type="number" v-model="itemData.token_lifetime"
                  :state="getValidationState(validationContext)" />

                <b-input-group-append>
                  <b-button variant="primary" @click="itemData.token_lifetime++">+</b-button>
                </b-input-group-append>
                <b-form-invalid-feedback>
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-input-group>
            </b-form-group>
          </validation-provider>

          <validation-provider #default="validationContext" :name="$t('profiles.inactivity_lifetime')" rules="required">
            <b-form-group class="text-capitalize" :label="$t('profiles.inactivity_lifetime')"
              label-for="inactivity_lifetime">
              <b-input-group>
                <b-input-group-prepend>
                  <b-button variant="primary" @click="itemData.inactivity_lifetime--"
                    :disabled="itemData.inactivity_lifetime <= 0 || itemData.inactivity_lifetime == null">-</b-button>
                </b-input-group-prepend>

                <b-form-input class="text-center" type="number" v-model="itemData.inactivity_lifetime"
                  :state="getValidationState(validationContext)" />

                <b-input-group-append>
                  <b-button variant="primary" @click="itemData.inactivity_lifetime++">+</b-button>
                </b-input-group-append>
                <b-form-invalid-feedback>
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-input-group>
            </b-form-group>
          </validation-provider>

          <validation-provider
            #default="validationContext"
            :name="$t('profiles.priority')"
            rules="required"
          >
            <b-form-group
              class="text-capitalize"
              :label="$t('profiles.priority')"
              label-for="token_lifetime"
            >
              <b-input-group>
                <b-input-group-prepend>
                  <b-button
                    variant="primary"
                    @click="itemData.priority--"
                    :disabled="itemData.priority <= 1 || itemData.priority == null"
                    >-</b-button
                  >
                </b-input-group-prepend>

                <b-form-input
                  class="text-center"
                  type="number"
                  v-model="itemData.priority"
                  :state="getValidationState(validationContext)"
                  min="1"
                  max="10"
                />

                <b-input-group-append>
                  <b-button variant="primary" @click="itemData.priority++" :disabled="itemData.priority >= 10"
                    >+</b-button
                  >
                </b-input-group-append>
                <b-form-invalid-feedback>
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-input-group>
              <small class="form-text text-muted">
                  {{$t('profiles.priority_helper')}}
                </small>
            </b-form-group>
          </validation-provider>

          <!-- Form Actions -->
          <div class="d-flex mt-2">
            <b-button v-ripple.400="'rgba(255, 255, 255, 0.15)'" variant="primary" class="mr-2 text-capitalize"
              type="submit" data-cy="submit">
              {{ $t("actions.save") }}
            </b-button>
            <b-button v-ripple.400="'rgba(186, 191, 199, 0.15)'" type="button" variant="outline-secondary" @click="hide"
              class="text-capitalize">
              {{ $t("actions.cancel") }}
            </b-button>
          </div>
        </b-form>
      </validation-observer>
    </template>
  </b-sidebar>
</template>

<script>
import {
  BSidebar,
  BForm,
  BFormGroup,
  BFormInput,
  BFormInvalidFeedback,
  BButton,
  BFormCheckbox,
  BFormTextarea
} from "bootstrap-vue";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import Treeselect from "@riophae/vue-treeselect";
import "@riophae/vue-treeselect/dist/vue-treeselect.css";
import vSelect from "vue-select";
import { ref, computed, watch, nextTick } from "@vue/composition-api";
import { required, alphaNum, email, positive, length } from "@validations";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import formValidation from "@core/comp-functions/forms/form-validation";
import Ripple from "vue-ripple-directive";
import store from "@/store";
import { useUtils as useI18nUtils } from "@core/libs/i18n";

export default {
  components: {
    Treeselect,
    BSidebar,
    BForm,
    BFormGroup,
    BFormInput,
    BFormInvalidFeedback,
    BButton,
    vSelect,
    BFormCheckbox,
    BFormTextarea,
    ValidationProvider,
    ValidationObserver,
  },
  directives: {
    Ripple,
  },
  model: {
    prop: "isSidebarActive",
    event: "update:is-sidebar-active",
  },
  props: {
    isSidebarActive: {
      type: Boolean,
      required: true,
    },
    itemEdit: {
      type: Object,
      required: false,
    },
    permissionsOptions: {
      type: Array,
      required: true,
    },
    layoutsOptions: {
      type: Array,
      required: true,
    },
    isAdd: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      required,
      alphaNum,
      email,
      positive,
    };
  },
  setup(props, { emit }) {
    const isDataLoaded = ref(false);
    const { t } = useI18nUtils();
    const trans = {
      "messages.edit_save.successfully_created": t(
        "messages.edit_save.successfully_created"
      ),
      "messages.edit_save.updated_successfully": t(
        "messages.edit_save.updated_successfully"
      ),
    };
    const blankData = {
      type: "",
      permissions: null,
      layouts: null,
      forced_layouts: false,
      token_lifetime: 8,
      priority: 10,
      inactivity_lifetime: 1,
    };
    const toast = useToast();
    const itemData = ref(JSON.parse(JSON.stringify(blankData)));
    const resetData = () => {
      itemData.value = JSON.parse(JSON.stringify(blankData));
    };

    const selectedPermissions = ref([]);

    const valueConsistsOf = ref("LEAF_PRIORITY");
    const allSelected = computed(() => {
      if (permissionsOptionsTree.value.length == 0) return false;
      const allIds = permissionsOptionsTree.value.flatMap((subject) =>
        subject.children.flatMap((child) =>
          // Incluir el ID del hijo...
          [child.id].concat(
            // Y los IDs de los nietos, si existen
            child.children
              ? child.children.map((grandchild) => grandchild.id)
              : []
          )
        )
      );
      // Realiza la comprobación estándar primero
      const standardCheck = selectedPermissions.value.length >= allIds.length;
      return standardCheck;
    });

    const permissionsArray = ref([]);
    const onSubmit = () => {
      let dispatch;

      if (props.isAdd) {
        itemData.value.permissions = selectedPermissions;
        dispatch = store.dispatch("profile/add", itemData.value);
      } else {
        const req = {
          id: itemData.value.id,
          params: {
            ...itemData.value,
            permissions: selectedPermissions.value
          }
        };
        dispatch = store.dispatch("profile/edit", req);
      }
      dispatch
        .then((response) => {
          if (response.success) {
            toast({
              component: ToastificationContent,
              props: {
                title: props.isAdd
                  ? trans["messages.edit_save.successfully_created"]
                  : trans["messages.edit_save.updated_successfully"],
                icon: "CheckIcon",
                variant: "success",
              },
            });
            emit("refetch-data");
            emit("update:is-sidebar-active", false);
          } else {
            toast({
              component: ToastificationContent,
              props: {
                title: response.message,
                icon: "AlertTriangleIcon",
                variant: "danger",
              },
            });
          }
        })
        .catch((response) => {
          if (response.response.status == 422) {
            let string = ``
            Object.entries(response.response.data.data).forEach(entry => {
              const [key, value] = entry
              string += `${key} - ${value}<br>`
            })
            toast({
              component: ToastificationContent,
              props: {
                title: `${response.response.data.message}`,
                icon: "AlertTriangleIcon",
                variant: "danger",
                text: string
              },
            }, {
              timeout: 10000
            }
            )
          } else {
            toast({
              component: ToastificationContent,
              props: {
                title: response.response.data.message,
                icon: "AlertTriangleIcon",
                variant: "danger",
              },
            })
          };
        });
    };

    const { refFormObserver, getValidationState, resetForm } =
      formValidation(resetData);

    const loadData = () => {
      if (!props.isAdd) {
        itemData.value = Object.assign({}, props.itemEdit);

        permissionsArray.value = props.itemEdit.permissions.map(
          (item) => item.id
        );

        selectedPermissions.value = itemData.value.permissions
          ? itemData.value.permissions.map((item) => item.id)
          : [];
      } else {
        selectedPermissions.value = [];
      }
    };

    const permissionsOptionsTree = ref([]);
    const widgetChilds = ref([]);

    const buildPermissionsOptionsTree = (permissionsOptions) => {
      if (permissionsOptions.length == 0) return [];

      const subjects = new Map();
      let workspaceVisualMenuWidgetsChild = null;
      //console.log(permissionsOptions);
      //Add each permission depending on the subject(resource)
      permissionsOptions.forEach((permission) => {
        if (!subjects.has(permission.subject)) {
          subjects.set(permission.subject, {
            id: permission.subject,
            label: permission.subject,
            children: [],
          });
        }
        const subject = subjects.get(permission.subject);
        subject.children.push({
          id: permission.id,
          label: permission.description,
        });
      });

      // Order childs alphabetical
      subjects.forEach((subject) => {
        subject.children.sort((a, b) => a.label.localeCompare(b.label));
      });

      //Search for the Workspace and child
      subjects.forEach((subject, key) => {
        if (key === "Workspace") {
          subject.children.forEach((child) => {
            if (child.id === 288) {
              //get the child in this case 288 is show menu widgets
              workspaceVisualMenuWidgetsChild = child;
              if (!workspaceVisualMenuWidgetsChild.children) {
                //create the children if it doesnt have already
                workspaceVisualMenuWidgetsChild.children = [];
              }
            }
          });
        }
      });

      //Move Widget to WorkSpace child
      if (workspaceVisualMenuWidgetsChild && subjects.has("Widget")) {
        const widgetSubject = subjects.get("Widget");
        if (widgetSubject.children) {
          workspaceVisualMenuWidgetsChild.children =
            workspaceVisualMenuWidgetsChild.children.concat(
              widgetSubject.children
            );
          //to value later in adding parent
          widgetChilds.value = workspaceVisualMenuWidgetsChild.children.map(
            (child) => child.id
          );
        }
        subjects.delete("Widget"); //Deletes the Subject Widget
      }

      //return it like a array so the library can use it and sort it
      return Array.from(subjects.values()).sort((a, b) =>
        a.label.localeCompare(b.label)
      );
    };

    const toggleAll = () => {
      if (allSelected.value) {
        selectedPermissions.value = [];
      } else {
        selectedPermissions.value = permissionsOptionsTree.value.flatMap(
          (subject) =>
            subject.children.flatMap((child) =>
              [child.id].concat(
                //id de los hijos del hijo
                child.children
                  ? child.children.map((grandchild) => grandchild.id)
                  : []
              )
            )
        );
      }
    };

    watch(
      () => props.permissionsOptions,
      (newValue, oldValue) => {
        if (!isDataLoaded.value && newValue && newValue.length > 0) {
          // console.log("buildPermissions");
          permissionsOptionsTree.value = buildPermissionsOptionsTree(newValue);
          isDataLoaded.value = true;
        }
      },
      { immediate: true }
    );

    watch(
      selectedPermissions,
      (newValue, oldValue) => {
        //console.log("entro a actualizar selected permissions: ", newValue);
        //console.log(widgetChilds.value);
        // Verificar si alguno de los IDs de los hijos está en la nueva lista y el ID del padre no está.
        const childIds = widgetChilds.value;
        const parentId = 288;

        const hasChildSelected = childIds.some((id) => newValue.includes(id));
        const hasParentSelected = newValue.includes(parentId);

        if (hasChildSelected && !hasParentSelected) {
          // Utilizamos nextTick para asegurarnos de que todas las mutaciones pendientes a selectedPermissions se hayan completado.
          nextTick(() => {
            // Es importante realizar una comprobación aquí para evitar un bucle infinito.
            if (!selectedPermissions.value.includes(parentId)) {
              selectedPermissions.value.push(parentId);
            }
          });
        }
      },
      { deep: true }
    );

    watch(
      () => itemData.value.token_lifetime,
      () => {
        if (itemData.value.token_lifetime <= 0) {
          itemData.value.token_lifetime = 1;
        }
      },
      { deep: true }
    )
    watch(
      () => itemData.value.inactivity_lifetime,
      () => {
        if (itemData.value.inactivity_lifetime <= 0) {
          itemData.value.inactivity_lifetime = 1;
        }
      },
    )

    return {
      permissionsArray,
      itemData,
      onSubmit,
      valueConsistsOf,
      selectedPermissions,
      allSelected,
      refFormObserver,
      getValidationState,
      loadData,
      resetForm,
      permissionsOptionsTree,
      buildPermissionsOptionsTree,
      toggleAll,
      isDataLoaded,
      widgetChilds,
    };
  },
};
</script>
